import { isAfter } from 'date-fns';
import { createSelector } from 'reselect';

import type { RootState } from 'config/store/reducers';
import type { Entity } from 'modules/entity/types';
import type { Dealer, Contact } from 'types/Dealer';
import type { Customer } from 'types/Global';
import type { MayBe } from 'types/common';

import { getPathName } from 'utils/utils';
import { getZonedEndOfDay } from 'utils/date';
import { PREVIEW } from 'constants/Parameters';
import { formatBrandName } from 'utils/string';
import { READ_ONLY_PAGES } from 'constants/Routes';
import { DEFAULT_UNITS } from 'constants/UnitConstants';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { getRefDataMedia } from 'modules/refdata/selectors';
import { getEntityType, READ_ONLY_STATUSES } from 'constants/EntityConstants';
import { groupPGA, PGA_DEALER_ID_EXCEPTIONS } from 'constants/DealerConstants';
import {
  FLEETBACK,
  FLEETBACK_ICON,
  MULTI_BRAND_NAME,
  DEFAULT_CURRENCY,
  DEFAULT_CURRENCY_ISO,
} from 'constants/Global';

const getState = (state: RootState) => state.entity;

const getUI = createSelector(getState, (state) => state.ui);

export const getEntity = createSelector(getState, (state): Entity => state.data ?? ({} as Entity));

export const getEntityId = createSelector(getEntity, (entity) => entity.id);

export const isLoading = createSelector(getUI, (ui) => ui.loading);

export const isSignatureModalOpen = createSelector(getUI, (ui) => ui.isSignatureModalOpen);

export const isSignHereModalOpen = createSelector(getUI, (ui) => ui.isSignHereModalOpen);

export const dismissedSignatureDocumentIds = createSelector(getUI, (ui) => ui.dismissedSignatureDocumentIds);

export const isReadOnly = createSelector(getEntity, (entity) => {
  const path = getPathName();
  return PREVIEW || READ_ONLY_STATUSES[getEntityType() ?? ''].includes(entity.status ?? '') || READ_ONLY_PAGES.includes(path);
});

export const isCustomizeEntity = createSelector(getEntity, (entity) => Boolean(entity.templateMode));

export const hasEntity = createSelector(getEntity, (entity) => entity && Object.keys(entity).length > 0);

export const hasError = createSelector(isLoading, hasEntity, (isFetching, hasData) => !isFetching && !hasData);

export const getDealer = createSelector(
  getEntity,
  (entity): Dealer => entity.dealer ?? ({} as Dealer)
);

export const getDealerId = createSelector(getDealer, dealer => dealer?.id);

export const getDealerCurrency = createSelector(getDealer, (dealer) => dealer.country?.currency ?? DEFAULT_CURRENCY);

export const getDealerCurrencyIso = createSelector(
  getDealer,
  (dealer) => dealer.country?.currencyIsoCode ?? DEFAULT_CURRENCY_ISO
);

export const getDealerValueAddedTax = createSelector(
  getDealer,
  (dealer) => dealer.valueAddedTax
);

export const getDefaultLanguage = createSelector(
  getDealer,
  (dealer) => dealer.customerDefaultLanguage ?? DEFAULT_LANGUAGE
);

const getCustomBranding = createSelector(getDealer, (dealer) => dealer.group?.customBranding ?? {});
export const getBrandingName = createSelector(getCustomBranding, (branding) => branding.name ?? FLEETBACK);
export const getFavicon = createSelector(getCustomBranding, (branding) => branding.faviconUrl ?? FLEETBACK_ICON);

const getBrand = createSelector(getEntity, (entity) => entity.brand ?? {});
export const getBrandLogoUrl = createSelector(
  getBrand,
  ({ publicLogoPath }): MayBe<string> => {
    if (typeof publicLogoPath !== 'string') {
      return undefined;
    }

    return publicLogoPath.startsWith('/') ? publicLogoPath : `/${publicLogoPath}`;
  }
);

export const getBrandBannerUrl = createSelector(
  getBrand,
  ({ publicBannerPath }): MayBe<string> => {
    if (typeof publicBannerPath !== 'string') {
      return undefined;
    }

    return publicBannerPath.startsWith('/') ? publicBannerPath : `/${publicBannerPath}`;
  }
);

export const getCustomerInfo = createSelector(
  getEntity,
  (entity): Customer => entity.customerInfo ?? ({} as Customer)
);

export const getBrandName = createSelector(getBrand, ({ name }) => name ?? MULTI_BRAND_NAME);
export const getFormattedBrand = createSelector(getBrandName, formatBrandName);

export const getDealerTimezone = createSelector(getDealer, (dealer) => dealer.timezone);

export const getCountryCode = createSelector(
  getDealer,
  (dealer) => dealer.country?.code ?? new Intl.Locale(navigator?.language).region
);

export const requiresConsentOnWithdrawalRights = createSelector(
  getDealer,
  (dealer) => dealer.country?.legal?.requireConsentOnWithdrawalRights ?? false
);

export const getLanguage = createSelector(
  getCustomerInfo,
  getDefaultLanguage,
  (customerInfo, defaultLanguage) => customerInfo.language ?? defaultLanguage
);

export const getPageParams = createSelector(
  getEntity,
  hasEntity,
  isLoading,
  hasError,
  (entity, hasData, isFetching, isError) => ({
    entity,
    isFetching,
    isReady: hasData,
    hasError: isError,
  })
);

// `groupId`/`group` are not declared anywhere on `Entity` — no current entity subtype (Intervention/
// Appointment/Audit/FollowUp/Template) carries them. Kept as-is (behavior-preserving fallback chain);
// see migration report — this may be dead legacy fallback predating `dealer.group.id`.
export const getGroupId = createSelector(
  getEntity,
  (entity: Entity & { groupId?: string; group?: { id?: string } }) =>
    entity.groupId ?? entity.group?.id ?? entity.dealer?.group?.id
);

export const isPGA = createSelector(
  getGroupId,
  getDealerId,
  (groupId, dealerId) =>
    // BMWVID-23611 - Enable specific dealer(s) with common FO display
    groupId === groupPGA && !PGA_DEALER_ID_EXCEPTIONS.includes(dealerId)

);

export const hasExpired = createSelector(getEntity, getDealerTimezone, ({ timeToReply }, timezone) =>
  (timeToReply ? isAfter(getZonedEndOfDay(new Date(), timezone), getZonedEndOfDay(timeToReply, timezone)) : false));

export const getContact = createSelector(getEntity, ({ contact }): Contact => contact ?? ({} as Contact));
export const getContactLinkAndTel = createSelector(getContact, ({ appointmentLink, tel }) => ({ appointmentLink, tel }));
export const hasBothLinkAndTel = createSelector(getContact, ({ appointmentLink, tel }) => Boolean(appointmentLink && tel));

export const getDealerName = createSelector(
  getContact,
  getDealer,
  (contact, dealer) => contact?.name ?? contact?.shortName ?? dealer?.name
);

export const shouldHidePreApprovedPrices = createSelector(getEntity, (entity) => entity.hidePreApprovedWorks ?? false);

export const getPayers = createSelector(getEntity, (entity) => entity.payers ?? []);

const getUnits = createSelector(getDealer, (dealer) => dealer?.units ?? DEFAULT_UNITS);
export const getSmallDistanceUnit = createSelector(getUnits, (units) => units?.smallDistanceUnit);
export const getLargeDistanceUnit = createSelector(getUnits, (units) => units?.largeDistanceUnit);

export const getChatMediaLimit = createSelector(
  getRefDataMedia,
  (refDataMedia) => {
    if (refDataMedia.message) {
      const { image, video, document } = refDataMedia.message;
      return [image, video, document].reduce((acc, media) => Math.min(media?.['max-files'] ?? Infinity, acc), Infinity);
    }
    return Infinity;
  }
);

export const getChatMediaAllowedContentTypes = createSelector(
  getRefDataMedia,
  (refDataMedia) => {
    if (refDataMedia.message) {
      const { image, video, document } = refDataMedia.message;
      const contentTypes = [image, video, document].reduce((acc, media) => {
        media?.['supported-types']?.split(',').forEach((t) => acc.add(t.trim()));
        return acc;
      }, new Set<string>());

      return Array.from(contentTypes);
    }
    return undefined;
  }
);
